import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Task, CreateTaskData, UpdateTaskData, TaskStatus, FilterOptions } from '@/types';
import { taskService } from '@/services/taskService';

interface TaskState {
  tasks: Task[];
  selectedTask: Task | null;
  filters: FilterOptions;
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
}

const initialState: TaskState = {
  tasks: [],
  selectedTask: null,
  filters: {},
  loading: false,
  error: null,
  lastFetch: null,
};

/**
 * Async Thunk: Fetch all tasks with filters
 */
export const fetchTasks = createAsyncThunk<Task[], FilterOptions>(
  'task/fetchTasks',
  async (filters, { rejectWithValue }) => {
    try {
      const tasks = await taskService.getTasks(filters);
      return tasks;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch tasks');
    }
  }
);

/**
 * Async Thunk: Fetch single task by ID
 */
export const fetchTaskById = createAsyncThunk<Task, string>(
  'task/fetchTaskById',
  async (taskId, { rejectWithValue }) => {
    try {
      const task = await taskService.getTaskById(taskId);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch task');
    }
  }
);

/**
 * Async Thunk: Create new task
 */
export const createTask = createAsyncThunk<Task, CreateTaskData>(
  'task/createTask',
  async (taskData, { rejectWithValue }) => {
    try {
      const task = await taskService.createTask(taskData);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create task');
    }
  }
);

/**
 * Async Thunk: Update task
 */
export const updateTask = createAsyncThunk<Task, { id: string; data: UpdateTaskData }>(
  'task/updateTask',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const task = await taskService.updateTask(id, data);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update task');
    }
  }
);

/**
 * Async Thunk: Delete task
 */
export const deleteTask = createAsyncThunk<string, string>(
  'task/deleteTask',
  async (taskId, { rejectWithValue }) => {
    try {
      await taskService.deleteTask(taskId);
      return taskId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete task');
    }
  }
);

/**
 * Async Thunk: Move task (change status)
 */
export const moveTask = createAsyncThunk<Task, { id: string; status: TaskStatus; position: number }>(
  'task/moveTask',
  async ({ id, status, position }, { rejectWithValue }) => {
    try {
      const task = await taskService.updateTask(id, { status, position });
      return task;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to move task');
    }
  }
);

/**
 * Task Slice
 * Manages task state, including CRUD operations and filtering
 */
const taskSlice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterOptions>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setSelectedTask: (state, action: PayloadAction<Task | null>) => {
      state.selectedTask = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    optimisticUpdateTask: (state, action: PayloadAction<{ id: string; data: Partial<Task> }>) => {
      const index = state.tasks.findIndex((task) => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = { ...state.tasks[index], ...action.payload.data };
      }
    },
    optimisticMoveTask: (state, action: PayloadAction<{ id: string; status: TaskStatus }>) => {
      const task = state.tasks.find((t) => t.id === action.payload.id);
      if (task) {
        task.status = action.payload.status;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Tasks
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
        state.lastFetch = Date.now();
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Task by ID
    builder
      .addCase(fetchTaskById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTask = action.payload;
        
        // Update in tasks array if exists
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(fetchTaskById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Task
    builder
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.push(action.payload);
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Task
    builder
      .addCase(updateTask.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.selectedTask?.id === action.payload.id) {
          state.selectedTask = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete Task
    builder
      .addCase(deleteTask.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((task) => task.id !== action.payload);
        if (state.selectedTask?.id === action.payload) {
          state.selectedTask = null;
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Move Task
    builder
      .addCase(moveTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setSelectedTask,
  clearError,
  optimisticUpdateTask,
  optimisticMoveTask,
} = taskSlice.actions;

export default taskSlice.reducer;
